import {
  Breadcrumbs,
  ModuleActionBar,
  RankModuleLayout,
  ReportModalLayout,
  TestAnswerList,
  TestCardsFrame,
} from '@components';
import {
  useModuleData,
  useNavigateWithOfflineMode,
  useScreenSize,
  useScrollToTop,
} from '@hooks';
import Net from '@pngs/OnboardingPage/Net.png';
import { testApi } from '@store';
import { Flex } from '@styles';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { certificationPageLayoutByVariant } from './CertificationPage.layoutProps';

const CertificationPage = () => {
  const location = useLocation();
  const { navigate } = useNavigateWithOfflineMode();
  const { device } = useScreenSize();

  const [isRankModalVisible, setRankModalVisible] = useState(false);
  const [rankModalContent, setRankModalContent] = useState(null);
  const [isReportModalVisible, setReportModalVisible] = useState(false);

  const [areAnswersShown, setAreAnswersShown] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTestPassed, setIsTestPassed] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [validatedAnswers, setValidatedAnswers] = useState(null);
  const [isValidated, setIsValidated] = useState(false);
  const { language } = useSelector((state) => state.authSlice);
  const layout = certificationPageLayoutByVariant[device || 'desktop'];

  const { refetch, test } = testApi.useGetTestByIdQuery(
    { id: location?.state?.id },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => ({
        test: data?.data,
      }),
      skip: !location?.state?.id,
    }
  );

  const { createTestProgress, isOffline, updateTestProgress, validateTest } =
    useModuleData({ moduleTest: test });

  useEffect(() => {
    refetch();
  }, [language]);

  const isTestFinished = answers?.length === test?.total_steps;

  const goBack = () => {
    navigate(-1);
  };

  const handleShowAnswers = () => {
    setAreAnswersShown(true);
  };

  const openRankModal = () => {
    setRankModalVisible(true);
  };

  const handleAnswer = (id, option) => async () => {
    if (currentStep === 0) {
      await createTestProgress({ testId: test?.id });
    }
    await updateTestProgress({
      passedStep: {
        chosenOptionId: option?.id,
        id,
      },
      testId: test?.id,
    });
    const newAnswers = [...answers, { optionId: option?.id, testStepId: id }];
    setAnswers(newAnswers);
    if (newAnswers?.length !== test?.total_steps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleValidateTest = async () => {
    if (isOffline) {
      const { data } = await validateTest({
        data: {
          testAnswers: answers,
        },
        id: test?.id,
      });
      setIsTestPassed(data?.is_passed);
      setCorrectAnswers(data?.correct_answers);
      setValidatedAnswers(data?.validated_answers);
      if (data?.is_passed) {
        setRankModalContent(data?.unlocked_entities);
        if (data?.unlocked_entities) {
          openRankModal();
        }
      }
    } else {
      const { data } = await validateTest({
        data: {
          testAnswers: answers,
        },
        id: test?.id,
      }).unwrap();
      setIsTestPassed(data?.is_passed);
      setCorrectAnswers(data?.correct_answers);
      setValidatedAnswers(data?.validated_answers);
      if (data?.is_passed) {
        setRankModalContent(data?.unlocked_entities);
        if (data?.unlocked_entities) {
          openRankModal();
        }
      }
    }
    setTimeout(() => {
      setIsValidated(true);
    }, 200);
  };

  const handleResetTest = () => {
    setAreAnswersShown(false);
    setAnswers([]);
    setCurrentStep(0);
  };

  const handleReportButtonClick = () => {
    setReportModalVisible(true);
  };

  useScrollToTop();

  useEffect(() => {
    if (isTestFinished) {
      handleValidateTest();
    }
  }, [answers, currentStep]);

  return (
    <Flex flexDirection="column">
      <ReportModalLayout
        activityId={location?.state?.id}
        isLessonActivity
        isVisible={isReportModalVisible}
        setIsVisible={setReportModalVisible}
        withBackdropNoise
      />
      <RankModuleLayout
        isVisible={isRankModalVisible}
        onClose={goBack}
        rank={rankModalContent?.new_rank}
        setIsVisible={setRankModalVisible}
      />
      <Breadcrumbs containerProps={{ px: layout?.px }} />
      <Flex
        backgroundImage={Net}
        backgroundSize="contain"
        pb={80}
        pt={24}
        px={layout.px}
      >
        <Flex flexDirection="column" width="100%">
          <ModuleActionBar
            additionalButton={{ isVisible: false }}
            progress={{ isSemivisible: true, percentage: 0 }}
            report={{
              isVisible: true,
              onClick: handleReportButtonClick,
              variant: 'report',
            }}
            sidePanel={{ isSemivisible: true }}
          />
          {areAnswersShown ? (
            <TestAnswerList
              handleGoBack={goBack}
              handleReset={handleResetTest}
              isTestFinished={isTestFinished}
              isTestPassed={isTestPassed}
              validatedAnswers={validatedAnswers}
            />
          ) : (
            <TestCardsFrame
              correctAnswers={correctAnswers} // TO DO validation
              data={test?.steps}
              handleGoBack={goBack}
              handleReset={handleResetTest}
              handleShowAnswers={handleShowAnswers}
              isTestFinished={isTestFinished}
              isTestPassed={isTestPassed}
              isValidated={isValidated}
              onLeftButtonPress={handleAnswer(
                test?.steps?.[currentStep]?.id,
                test?.steps?.[currentStep]?.options?.[1]
              )}
              onRightButtonPress={handleAnswer(
                test?.steps?.[currentStep]?.id,
                test?.steps?.[currentStep]?.options?.[0]
              )}
              withCornerNumbers
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CertificationPage;
